import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from 'shared/core';
import { Modules, Responsibility, DashboardHubCard } from 'shared/models';

@Component({
    selector: 'app-dashboard-hub',
    templateUrl: './DashboardHub.component.html',
    styleUrls: ['./DashboardHub.component.css'],
    standalone: false
})
export class DashboardHubComponent implements OnInit {
  ApiUrl = environment.Reporting_Api_Url;

  // Dashboard cards with permission-filtered visibility
  dashboardCards: DashboardHubCard[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: AuthService
  ) { }

  ngOnInit(): void {
    this.initializeDashboardCards();
  }

  private initializeDashboardCards(): void {
    // Define all dashboards with their permissions
    const allCards: DashboardHubCard[] = [
      {
        id: 'production',
        title: 'Production Dashboard',
        description: 'Monitor order pipeline, post-process stages, inspection, dispatch, and costing status across the production floor.',
        icon: 'tool',
        color: '#1890ff',
        route: '/home/dashboard/production',
        modulePermission: Modules.ProductionDashboard,
        headlineTiles: [],
        isLoading: true
      },
      {
        id: 'inventory',
        title: 'Inventory Dashboard',
        description: 'Track pending inspections, quality checks, stock allocations, out-pass operations, and product configuration health.',
        icon: 'database',
        color: '#52c41a',
        route: '/home/dashboard/inventory',
        modulePermission: Modules.InventoryDashboard,
        headlineTiles: [],
        isLoading: true
      },
      {
        id: 'performance',
        title: 'Production Performance',
        description: 'Identify delayed orders, operational bottlenecks, dispatch tracking, and quality metrics to improve production efficiency.',
        icon: 'dashboard',
        color: '#fa8c16',
        route: '/home/dashboard/performance',
        modulePermission: Modules.ProductionPerformanceDashboard,
        headlineTiles: [],
        isLoading: true
      },
      {
        id: 'dispatch',
        title: 'Dispatch Dashboard',
        description: 'Monitor dispatch pipeline, cycle times, gate operations, and delivery performance metrics.',
        icon: 'export',
        color: '#13c2c2',
        route: '/home/dashboard/dispatch',
        modulePermission: Modules.DispatchDashboard,
        headlineTiles: [],
        isLoading: true
      },
      {
        id: 'sales',
        title: 'Sales Dashboard',
        description: 'Overview of orders received, order value analysis, customer insights, and fulfillment tracking.',
        icon: 'shopping-cart',
        color: '#722ed1',
        route: '/home/dashboard/sales',
        modulePermission: Modules.SalesDashboard,
        headlineTiles: [],
        isLoading: true
      },
      {
        id: 'reporting',
        title: 'Reporting Dashboard',
        description: 'Gate operations, purchase order analytics, and product management metrics with customizable layout.',
        icon: 'bar-chart',
        color: '#13c2c2',
        route: '/home/dashboard/reporting',
        modulePermission: Modules.ReportingDashboard,
        headlineTiles: [],
        isLoading: true
      },
      {
        id: 'store-capacity',
        title: 'Store Capacity Management',
        description: 'Visualize warehouse store utilization, rack capacity, and storage availability across all stores.',
        icon: 'appstore',
        color: '#eb2f96',
        route: '/home/dashboard/store-capacity-management',
        modulePermission: Modules.StoreCapacityManagement,
        headlineTiles: [],
        isLoading: false
      }
    ];

    // Filter by user permissions
    this.dashboardCards = allCards.filter(card =>
      this.auth.CheckResponsibility(card.modulePermission as Modules, Responsibility.View)
    );

    // Load headline counts for each visible dashboard
    this.loadHeadlineCounts();
  }

  // Load summary counts for each dashboard card
  private loadHeadlineCounts(): void {
    for (const card of this.dashboardCards) {
      this.loadCardHeadlines(card);
    }
  }

  private loadCardHeadlines(card: DashboardHubCard): void {
    // Skip headline loading for dashboards without headline endpoints
    if (!card.isLoading) return;

    const url = this.ApiUrl + `report/${card.id}dashboard-headlines`;

    this.http.post<any>(url, { DateFilterType: 'last7days' }).subscribe({
      next: (res) => {
        if (res) {
          card.headlineTiles = this.extractHeadlines(card.id, res);
        }
        card.isLoading = false;
      },
      error: () => {
        // Headline loading is optional — show card without counts if API fails
        card.isLoading = false;
      }
    });
  }

  // Extract headline metrics from API response for card display
  private extractHeadlines(dashboardId: string, data: any): { label: string; value: number; color?: string }[] {
    switch (dashboardId) {
      case 'production':
        return [
          { label: 'New Orders', value: data.NewOrdersCount || 0 },
          { label: 'In Production', value: data.InProductionCount || 0 },
          { label: 'Post Process', value: data.InPostProcessCount || 0, color: '#1890ff' }
        ];
      case 'inventory':
        return [
          { label: 'Pending Inspection', value: data.PendingInspectionCount || 0 },
          { label: 'Pending Allocation', value: data.PendingAllocationCount || 0 },
          { label: 'Out Pass Pending', value: data.PendingOutPassCount || 0, color: '#fa8c16' }
        ];
      case 'performance':
        return [
          { label: 'Delayed Orders', value: data.DelayedOrdersCount || 0, color: '#ff4d4f' },
          { label: 'Approaching Deadline', value: data.ApproachingDeadlineCount || 0, color: '#fa8c16' },
          { label: 'Pending Costing', value: data.PendingCostingCount || 0 }
        ];
      case 'sales':
        return [
          { label: 'Orders Received', value: data.OrdersReceivedCount || 0 },
          { label: 'Active Orders', value: data.ActiveOrdersCount || 0 },
          { label: 'On-Time %', value: data.OnTimeDeliveryPercentage || 0, color: '#52c41a' }
        ];
      case 'reporting':
        return [
          { label: 'Pending Gate Out', value: data.PendingGateOutCount || 0 },
          { label: 'Active POs', value: data.ActivePOCount || 0 },
          { label: 'Low Stock', value: data.LowStockCount || 0, color: '#fa8c16' }
        ];
      case 'dispatch':
        return [
          { label: 'Pending Dispatch', value: data.PendingDispatchCount || 0 },
          { label: 'Delayed', value: data.DelayedCount || 0, color: '#ff4d4f' },
          { label: 'Gate Out Pending', value: data.GateOutPendingCount || 0, color: '#fa8c16' }
        ];
      default:
        return [];
    }
  }

  // Navigate to the selected dashboard
  navigateToDashboard(card: DashboardHubCard): void {
    this.router.navigate([card.route]);
  }
}
